import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import moment from "moment";
import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
} from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import Loading from "../../components/Loading/Loading";

const getField = (data, flatKey, nestedPath) => {
  if (!data) return null;
  if (data[flatKey] !== undefined && data[flatKey] !== null) return data[flatKey];
  const parts = nestedPath.split(".");
  return parts.reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : null), data);
};

const getMpan = (data) => getField(data, "mpan_core", "metadata.mpan_core");
const getRequestId = (data) => getField(data, "request_id", "metadata.request_id");
const getObjectionWindowEnd = (data) =>
  getField(data, "objection_window_end_date", "request_data.objection_window_end_date");
const getAnnulmentWindowEnd = (data) =>
  getField(data, "annulment_window_end_date", "request_data.annulment_window_end_date");

const formatDate = (value) => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : value;
};

const extractPage = (data) => ({
  rows: Array.isArray(data) ? data : (data && data.content) || [],
  total: (data && (data.totalElements || (data.page && data.page.totalElements))) || 0,
});

const buildRows = (steps, getDate) =>
  steps.map((step, index) => ({
    id: step.id || `${getMpan(step.data)}-${index}`,
    rowNumber: index + 1,
    mpan: getMpan(step.data) || "",
    date: getDate(step.data),
    requestId: getRequestId(step.data),
  }));

const LossInterventionDashboard = () => {
  // ── Objection table state ───────────────────────────────────────────────
  const [objectionRows, setObjectionRows] = useState([]);
  const [objectionTotal, setObjectionTotal] = useState(0);
  const [objectionLoading, setObjectionLoading] = useState(false);
  const [objectionError, setObjectionError] = useState(null);
  const [objectionPage, setObjectionPage] = useState(1);
  const [objectionSize, setObjectionSize] = useState(10);
  const [objectionSortField, setObjectionSortField] = useState(null);
  const [objectionSortOrder, setObjectionSortOrder] = useState(null);
  const [objectionSearch, setObjectionSearch] = useState("");

  // ── Annulment table state ───────────────────────────────────────────────
  const [annulmentRows, setAnnulmentRows] = useState([]);
  const [annulmentTotal, setAnnulmentTotal] = useState(0);
  const [annulmentLoading, setAnnulmentLoading] = useState(false);
  const [annulmentError, setAnnulmentError] = useState(null);
  const [annulmentPage, setAnnulmentPage] = useState(1);
  const [annulmentSize, setAnnulmentSize] = useState(10);
  const [annulmentSortField, setAnnulmentSortField] = useState(null);
  const [annulmentSortOrder, setAnnulmentSortOrder] = useState(null);
  const [annulmentSearch, setAnnulmentSearch] = useState("");

  // ── Submit state ────────────────────────────────────────────────────────
  const [submitLoadingId, setSubmitLoadingId] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(null);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.token}` });

  // ── Fetch functions ─────────────────────────────────────────────────────
  const fetchObjectionWindow = useCallback(async () => {
    setObjectionLoading(true);
    setObjectionError(null);
    try {
      const params = {
        page: objectionPage - 1,
        size: objectionSize,
      };
      if (objectionSortField) {
        params.sortBy = objectionSortField;
        params.sortDir = objectionSortOrder;
      }
      const { data } = await axios.get("/df/api/supplier-loss/objection-window", {
        headers: authHeader(),
        params,
      });
      const { rows, total } = extractPage(data);
      setObjectionRows(buildRows(rows, getObjectionWindowEnd));
      setObjectionTotal(total);
    } catch (e) {
      setObjectionError(
        (e && e.response && e.response.data && e.response.data.message) ||
          e.message ||
          "Failed to load pending loss flows."
      );
    } finally {
      setObjectionLoading(false);
    }
  }, [objectionPage, objectionSize, objectionSortField, objectionSortOrder]);

  const fetchAnnulmentWindow = useCallback(async () => {
    setAnnulmentLoading(true);
    setAnnulmentError(null);
    try {
      const params = {
        page: annulmentPage - 1,
        size: annulmentSize,
      };
      if (annulmentSortField) {
        params.sortBy = annulmentSortField;
        params.sortDir = annulmentSortOrder;
      }
      const { data } = await axios.get("/df/api/supplier-loss/annulment-window", {
        headers: authHeader(),
        params,
      });
      const { rows, total } = extractPage(data);
      setAnnulmentRows(buildRows(rows, getAnnulmentWindowEnd));
      setAnnulmentTotal(total);
    } catch (e) {
      setAnnulmentError(
        (e && e.response && e.response.data && e.response.data.message) ||
          e.message ||
          "Failed to load annulment-eligible flows."
      );
    } finally {
      setAnnulmentLoading(false);
    }
  }, [annulmentPage, annulmentSize, annulmentSortField, annulmentSortOrder]);

  useEffect(() => { fetchObjectionWindow(); }, [fetchObjectionWindow]);
  useEffect(() => { fetchAnnulmentWindow(); }, [fetchAnnulmentWindow]);

  // ── Table change handlers ───────────────────────────────────────────────
  const handleObjectionTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {
    if (type === 'pagination') {
      setObjectionPage(page);
      setObjectionSize(sizePerPage);
    } else if (type === 'sort') {
      setObjectionSortField(sortField);
      setObjectionSortOrder(sortOrder);
      setObjectionPage(1);
    }
  };

  const handleAnnulmentTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {
    if (type === 'pagination') {
      setAnnulmentPage(page);
      setAnnulmentSize(sizePerPage);
    } else if (type === 'sort') {
      setAnnulmentSortField(sortField);
      setAnnulmentSortOrder(sortOrder);
      setAnnulmentPage(1);
    }
  };

  const submitIntervention = async ({ mpan, requestId, interventionType }) => {
    setSubmitError(null);
    setSubmitSuccess(null);
    setSubmitLoadingId(`${requestId}-${interventionType}`);
    try {
      await axios.post(
        "/df/api/supplier-loss/submit-intervention",
        {
          mpan_core: mpan,
          request_id: requestId,
          intervention_type: interventionType,
        },
        { headers: { ...authHeader(), "Content-Type": "application/json" } }
      );

      const actionLabel =
        interventionType === "NoObjection" ? "No Objection" : interventionType;
      setSubmitSuccess(`${actionLabel} submitted successfully for MPAN ${mpan}.`);

      if (interventionType === "Annulment") {
        await fetchAnnulmentWindow();
      } else {
        await fetchObjectionWindow();
      }
    } catch (e) {
      setSubmitError(
        (e && e.response && e.response.data && e.response.data.message) ||
          e.message ||
          "Submission failed. Please try again."
      );
    } finally {
      setSubmitLoadingId(null);
    }
  };

  const objectionColumns = [
    {
      dataField: "rowNumber",
      text: "Row",
      sort: false,
      headerStyle: { width: "80px" },
    },
    {
      dataField: "mpan",
      text: "MPAN",
      sort: true,
    },
    {
      dataField: "date",
      text: "Objection Window End Date",
      sort: true,
      formatter: (cell) => formatDate(cell),
    },
    {
      dataField: "actions",
      text: "Action",
      sort: false,
      formatter: (_, row) => (
        <div>
          <Button
            size='sm'
            color='danger'
            className='mr-2'
            disabled={submitLoadingId === `${row.requestId}-Objection`}
            onClick={() =>
              submitIntervention({
                mpan: row.mpan,
                requestId: row.requestId,
                interventionType: "Objection",
              })
            }
          >
            {submitLoadingId === `${row.requestId}-Objection` ? <i className='fa fa-spinner fa-spin' /> : "Objection"}
          </Button>
          <Button
            size='sm'
            color='success'
            disabled={submitLoadingId === `${row.requestId}-NoObjection`}
            onClick={() =>
              submitIntervention({
                mpan: row.mpan,
                requestId: row.requestId,
                interventionType: "NoObjection",
              })
            }
          >
            {submitLoadingId === `${row.requestId}-NoObjection` ? <i className='fa fa-spinner fa-spin' /> : "No Objection"}
          </Button>
        </div>
      ),
    },
  ];

  const annulmentColumns = [
    {
      dataField: "rowNumber",
      text: "Row",
      sort: false,
      headerStyle: { width: "80px" },
    },
    {
      dataField: "mpan",
      text: "MPAN",
      sort: true,
    },
    {
      dataField: "date",
      text: "Annulment Window End Date",
      sort: true,
      formatter: (cell) => formatDate(cell),
    },
    {
      dataField: "actions",
      text: "Action",
      sort: false,
      formatter: (_, row) => (
        <Button
          size='sm'
          color='warning'
          disabled={submitLoadingId === `${row.requestId}-Annulment`}
          onClick={() =>
            submitIntervention({
              mpan: row.mpan,
              requestId: row.requestId,
              interventionType: "Annulment",
            })
          }
        >
          {submitLoadingId === `${row.requestId}-Annulment` ? <i className='fa fa-spinner fa-spin' /> : "Annulment"}
        </Button>
      ),
    },
  ];

  const filteredObjectionRows = objectionRows.filter((row) =>
    String(row.mpan || "").toLowerCase().includes(objectionSearch.toLowerCase())
  );

  const filteredAnnulmentRows = annulmentRows.filter((row) =>
    String(row.mpan || "").toLowerCase().includes(annulmentSearch.toLowerCase())
  );

  return (
    <div className='animated fadeIn'>
      <Row className='subheader'>
        <Col className='col-8'>
          <h1>Loss Intervention Dashboard</h1>
        </Col>
      </Row>

      <div className='page-cnt'>
        {submitLoadingId && (
          <Alert color='info'>
            <i className='fa fa-spinner fa-spin mr-2' />
            Submitting intervention...
          </Alert>
        )}
        {submitSuccess && <Alert color='success'>{submitSuccess}</Alert>}
        {submitError && <Alert color='danger'>{submitError}</Alert>}

        <Row className='mb-4'>
          <Col>
            <h3>Pending Loss Flows</h3>
          </Col>
        </Row>
        <Row className='align-items-end mb-3'>
          <Col xs='12' md='4'>
            <FormGroup>
              <Label>Search by MPAN</Label>
              <Input
                type='text'
                placeholder='Enter MPAN'
                value={objectionSearch}
                onChange={(e) => setObjectionSearch(e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row className='mb-5'>
          <Col>
            {objectionLoading && <Loading />}
            <BootstrapTable
              keyField='id'
              classes='table-hover'
              wrapperClasses='table-responsive'
              noDataIndication={() => <div>No records found.</div>}
              columns={objectionColumns}
              data={filteredObjectionRows}
              bordered={false}
              hover
              remote={{ pagination: true, sort: true, filter: false }}
              onTableChange={handleObjectionTableChange}
              pagination={paginationFactory({
                page: objectionPage,
                sizePerPage: objectionSize,
                totalSize: objectionTotal,
                showTotal: true,
                sizePerPageList: [
                  { text: "10", value: 10 },
                  { text: "20", value: 20 },
                  { text: "50", value: 50 },
                ],
              })}
            />
            {objectionError && <Alert color='danger' className='mt-3'>{objectionError}</Alert>}
          </Col>
        </Row>

        <Row className='mb-4'>
          <Col>
            <h3>Loss Flows Eligible for Annulment</h3>
          </Col>
        </Row>
        <Row className='align-items-end mb-3'>
          <Col xs='12' md='4'>
            <FormGroup>
              <Label>Search by MPAN</Label>
              <Input
                type='text'
                placeholder='Enter MPAN'
                value={annulmentSearch}
                onChange={(e) => setAnnulmentSearch(e.target.value)}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col>
            {annulmentLoading && <Loading />}
            <BootstrapTable
              keyField='id'
              classes='table-hover'
              wrapperClasses='table-responsive'
              noDataIndication={() => <div>No records found.</div>}
              columns={annulmentColumns}
              data={filteredAnnulmentRows}
              bordered={false}
              hover
              remote={{ pagination: true, sort: true, filter: false }}
              onTableChange={handleAnnulmentTableChange}
              pagination={paginationFactory({
                page: annulmentPage,
                sizePerPage: annulmentSize,
                totalSize: annulmentTotal,
                showTotal: true,
                sizePerPageList: [
                  { text: "10", value: 10 },
                  { text: "20", value: 20 },
                  { text: "50", value: 50 },
                ],
              })}
            />
            {annulmentError && <Alert color='danger' className='mt-3'>{annulmentError}</Alert>}
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default LossInterventionDashboard;